import { AnimatePresence, motion } from 'motion/react';

import { Icons } from '@/assets/icons/icons';
import { Button, XStack } from '@/components/ui';
import { BulkMoveToProjectDropdown } from '@/features/projects/move-to-project';

export function ChatBulkActions({
  selectedIds,
  confirmingDelete,
  isArchiving,
  isDeleting,
  archiveMode,
  onClear,
  onArchiveOrRestore,
  onDelete,
}: {
  selectedIds: Set<string>;
  confirmingDelete: boolean;
  isArchiving: boolean;
  isDeleting: boolean;
  archiveMode: 'archive' | 'unarchive';
  onClear: () => void;
  onArchiveOrRestore: () => void;
  onDelete: () => void;
}) {
  const selectionActive = selectedIds.size > 0;
  const isBusy = isArchiving || isDeleting;

  return (
    <AnimatePresence>
      {selectionActive && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.15 }}
          className="absolute bottom-12 left-0 right-0 flex justify-center z-10 pointer-events-none"
        >
          <XStack className="items-center gap-2 pointer-events-auto">
            <Button
              size="sm"
              variant="secondary"
              onClick={onClear}
              disabled={isBusy}
              className="gap-1.5"
            >
              {selectedIds.size} selected
              <Icons.cancel className="size-3" />
            </Button>
            <BulkMoveToProjectDropdown
              targets={[...selectedIds].map((id) => ({
                kind: 'thread' as const,
                id,
              }))}
              itemNoun="chat"
              onComplete={onClear}
              disabled={isBusy}
            />
            <Button
              size="sm"
              variant="secondary"
              onClick={onArchiveOrRestore}
              isLoading={isArchiving}
              disabled={isBusy}
              className="gap-1.5"
            >
              {archiveMode === 'unarchive' ? (
                <Icons.restoreDocument className="size-3.5" />
              ) : (
                <Icons.archive className="size-3.5" />
              )}
              {archiveMode === 'unarchive' ? 'Restore' : 'Archive'}
            </Button>
            <Button
              size="sm"
              variant={confirmingDelete ? 'destructive' : 'secondary'}
              onClick={onDelete}
              isLoading={isDeleting}
              disabled={isBusy}
              className="gap-1.5"
            >
              <Icons.trash className="size-3.5" />
              {confirmingDelete ? 'Confirm?' : 'Delete'}
            </Button>
          </XStack>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
